import * as runtimeEnvironment from 'env';
import { UnreachableError } from 'hellospa/ts-utils';

export const siteCodeF = 'F';
export const siteCodeS = 'S';
export const siteCodeW = 'W';

export type SiteCodeType = typeof siteCodeF | typeof siteCodeS | typeof siteCodeW;

/**
 * Helper function which asserts that a site code is valid.
 * If the given site code isn't less generic than a string,
 * the developer can assert that it is a valid site code
 * using this function, rather than potentially dangerously
 * casting it as a SiteCodeType using "as SiteCodeType".
 *
 * @author Nathan Buchar <nbuchar@dropbox.com>
 * @throws {TypeError} siteCode must be valid.
 * @example
 *
 * ```
 * assertValidSiteCode(siteCode);
 * const brandName = getBrandName(siteCode);
 * ```
 */
export function assertValidSiteCode(siteCode: string): asserts siteCode is SiteCodeType {
  if (![siteCodeF, siteCodeS, siteCodeW].includes(siteCode)) {
    throw new TypeError(`Invalid site code ${siteCode}`);
  }
}

/**
 * Returns the brand name for the given site code.
 *
 * @author Nathan Buchar <nbuchar@dropbox.com>
 * @example
 *
 * ```
 * <FormattedMessage
 *   id=""
 *   description="..."
 *   defaultMessage="Subscribe to {brandName} updates"
 *   values={{
 *     brandName: getBrandName(siteCode),
 *   }} />
 * ```
 */
export function getBrandName(siteCode: SiteCodeType) {
  switch (siteCode) {
    case siteCodeF:
      return runtimeEnvironment.hfName;
    case siteCodeS:
      return runtimeEnvironment.hsName;
    case siteCodeW:
      return runtimeEnvironment.hwName;
    default:
      throw new UnreachableError(siteCode);
  }
}
